import React, { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Stack } from "@mui/material";
import { useAccountSelector, useReportsQueriesSelector } from "store";

import { useGroupByContent } from "../../../hooks";
import { ReportsQueries, RowBy } from "../../../store/types";
import { MaterialTooltip } from "../../tooltips/MaterialTooltip";
import { AutocompleteMultiSelect, SelectOption } from "../AutocompleteMultiSelect/AutocompleteMultiSelect";
import { AutocompleteMeasures } from "../Set-table-columns/AutocompleteMeasures";

export type DataFormation = Partial<Pick<ReportsQueries, "groupBy" | "rowBy">>;

// Row by options (for Last Group)
const rowByOptions: SelectOption<RowBy>[] = [
  { text: "Investor", key: "Investor", value: "investor" },
  { text: "Deal", key: "Deal", value: "deal" },
  { text: "Measure", key: "Measure", value: "measure" }
];

interface SetTableGroupByProps {
  defaultQueries: Partial<ReportsQueries>;
  onChangeDataFormation?: (dataFormation: DataFormation) => void;
  groupByOptions: SelectOption[];
}

export const ReportSetTableGroupBy: React.FC<SetTableGroupByProps> = ({
  defaultQueries,
  onChangeDataFormation,
  groupByOptions
}) => {
  const fields: Array<keyof ReportsQueries> = ["measures", "calculatedFields"];

  const { setFieldValue, values } = useGroupByContent({
    fields,
    onChangeDataFormation,
    defaultQueries,
    groupByFieldName: "groupBy"
  });

  const { originalCurrency } = useReportsQueriesSelector();
  const { allow_extended_groups } = useAccountSelector() || {};

  const noneOption = { text: "None", key: "None", value: "none" };
  const currencyOption = { text: "Currency", key: "Currency", value: "Currency" };

  // When originalCurrency is ON, max user groups = 5 (Currency + 5 user groups = 6 total)
  // When originalCurrency is OFF, max groups = 4
  const maxGroups = originalCurrency ? 5 : 4;

  // State for tracking visible groupBy groups
  // Feature flag OFF: 2 base groups (Group 1, Group 2) + Last Group (rowBy) = 3 total
  // Feature flag ON: 2 base + up to 2/3 extended + Last Group (rowBy) = 5/6 total (depending on originalCurrency)
  const [visibleGroups, setVisibleGroups] = useState<number>(() => {
    let count = 2; // Start with 2 base groups
    if (allow_extended_groups) {
      if (values.thirdGroupBy && values.thirdGroupBy !== "none") count = 3;
      if (values.fourthGroupBy && values.fourthGroupBy !== "none") count = 4;
      if (values.fifthGroupBy && values.fifthGroupBy !== "none") count = 5;
    }
    return count;
  });

  // Track previous originalCurrency state for shift logic
  const [prevOriginalCurrency, setPrevOriginalCurrency] = useState(originalCurrency);

  // When originalCurrency is toggled, shift groups accordingly
  useEffect(() => {
    if (originalCurrency !== prevOriginalCurrency) {
      if (originalCurrency && values.firstGroupBy !== "Currency") {
        // Turning ON: shift all groups down by 1, set firstGroupBy to "Currency"
        setFieldValue("fifthGroupBy", values.fourthGroupBy);
        setFieldValue("fourthGroupBy", values.thirdGroupBy);
        setFieldValue("thirdGroupBy", values.secondGroupBy);
        setFieldValue("secondGroupBy", values.firstGroupBy);
        setFieldValue("firstGroupBy", "Currency");
      } else if (!originalCurrency && values.firstGroupBy === "Currency") {
        // Turning OFF: shift all groups up by 1, remove Currency from firstGroupBy
        setFieldValue("firstGroupBy", values.secondGroupBy);
        setFieldValue("secondGroupBy", values.thirdGroupBy);
        setFieldValue("thirdGroupBy", values.fourthGroupBy);
        setFieldValue("fourthGroupBy", values.fifthGroupBy);
        setFieldValue("fifthGroupBy", undefined);
      }
      setPrevOriginalCurrency(originalCurrency);
    }
  }, [originalCurrency, prevOriginalCurrency, values, setFieldValue]);

  const handleAddGroup = () => {
    if (visibleGroups < maxGroups) {
      setVisibleGroups((prev) => prev + 1);
    }
  };

  const handleRemoveGroup = (groupIndex: 3 | 4 | 5) => {
    if (groupIndex === 5) {
      // Remove Group 5
      setFieldValue("fifthGroupBy", undefined);
      setVisibleGroups((prev) => Math.max(2, prev - 1));
    } else if (groupIndex === 4) {
      // Remove Group 4 - shift Group 5 value to Group 4 if it exists
      if (visibleGroups === 5) {
        setFieldValue("fourthGroupBy", values.fifthGroupBy);
        setFieldValue("fifthGroupBy", undefined);
      } else {
        setFieldValue("fourthGroupBy", undefined);
      }
      setVisibleGroups((prev) => Math.max(2, prev - 1));
    } else if (groupIndex === 3) {
      // Remove Group 3 - shift Group 4 and 5 values up if they exist
      if (visibleGroups === 5) {
        setFieldValue("thirdGroupBy", values.fourthGroupBy);
        setFieldValue("fourthGroupBy", values.fifthGroupBy);
        setFieldValue("fifthGroupBy", undefined);
      } else if (visibleGroups === 4) {
        setFieldValue("thirdGroupBy", values.fourthGroupBy);
        setFieldValue("fourthGroupBy", undefined);
      } else {
        setFieldValue("thirdGroupBy", undefined);
      }
      setVisibleGroups((prev) => Math.max(2, prev - 1));
    }
  };

  const effectiveFirstGroupByOptions = useMemo(() => {
    let filtered = groupByOptions.filter((option) => values.secondGroupBy !== option.value);

    // Hide "Currency" option when originalCurrency is ON (it's auto-managed)
    if (originalCurrency) {
      filtered = filtered.filter((opt) => opt.value !== "Currency");
    }

    return filtered;
  }, [groupByOptions, values, originalCurrency]);

  const effectiveSecondGroupByOptions = useMemo(() => {
    let filtered = groupByOptions.filter((option) => values.firstGroupBy !== option.value);

    // Hide "Currency" option when originalCurrency is ON (it's auto-managed)
    if (originalCurrency) {
      filtered = filtered.filter((opt) => opt.value !== "Currency");
    }

    return filtered;
  }, [groupByOptions, values, originalCurrency]);

  const effectiveThirdGroupByOptions = useMemo(() => {
    let filtered = groupByOptions.filter(
      (option) => values.firstGroupBy !== option.value && values.secondGroupBy !== option.value
    );

    // Hide "Currency" option when originalCurrency is ON (it's auto-managed)
    if (originalCurrency) {
      filtered = filtered.filter((opt) => opt.value !== "Currency");
    }

    return filtered;
  }, [groupByOptions, values, originalCurrency]);

  const effectiveFourthGroupByOptions = useMemo(() => {
    let filtered = groupByOptions.filter(
      (option) =>
        values.firstGroupBy !== option.value &&
        values.secondGroupBy !== option.value &&
        values.thirdGroupBy !== option.value
    );

    if (originalCurrency) {
      filtered = filtered.filter((opt) => opt.value !== "Currency");
    }

    return filtered;
  }, [groupByOptions, values, originalCurrency]);

  const effectiveFifthGroupByOptions = useMemo(() => {
    let filtered = groupByOptions.filter(
      (option) =>
        values.firstGroupBy !== option.value &&
        values.secondGroupBy !== option.value &&
        values.thirdGroupBy !== option.value &&
        values.fourthGroupBy !== option.value
    );

    if (originalCurrency) {
      filtered = filtered.filter((opt) => opt.value !== "Currency");
    }

    return filtered;
  }, [groupByOptions, values, originalCurrency]);

  const [firstGroupByOption, secondGroupByOption, thirdGroupByOption, fourthGroupByOption, fifthGroupByOption] =
    useMemo(() => {
      return [
        groupByOptions.find((opt) => values.firstGroupBy === opt.value),
        groupByOptions.find((opt) => values.secondGroupBy === opt.value),
        groupByOptions.find((opt) => values.thirdGroupBy === opt.value),
        groupByOptions.find((opt) => values.fourthGroupBy === opt.value),
        groupByOptions.find((opt) => values.fifthGroupBy === opt.value)
      ];
    }, [groupByOptions, values]);

  const rowByOption = useMemo(() => {
    return rowByOptions.find((opt) => values.rowBy === opt.value) || rowByOptions[0];
  }, [values.rowBy]);

  const groupByHasMeasures =
    values.firstGroupBy === "measure" ||
    values.secondGroupBy === "measure" ||
    values.thirdGroupBy === "measure" ||
    values.fourthGroupBy === "measure" ||
    values.fifthGroupBy === "measure" ||
    values.rowBy === "measure";

  return (
    <div className="group-by-content">
      <div className="group-by-content__title">Group by</div>

      {/* Group 1 - always visible. When originalCurrency is ON, shows Currency and is disabled */}
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={originalCurrency ? currencyOption : (firstGroupByOption ?? noneOption)}
            label="Group 1"
            options={originalCurrency ? [currencyOption] : effectiveFirstGroupByOptions}
            onChange={(value) => setFieldValue("firstGroupBy", value)}
            stateAfterClear={noneOption}
            disabled={originalCurrency && values.firstGroupBy === "Currency"}
          />
        </div>
      </div>

      {/* Group 2 - always visible */}
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={secondGroupByOption ?? noneOption}
            label="Group 2"
            options={effectiveSecondGroupByOptions}
            onChange={(value) => setFieldValue("secondGroupBy", value)}
            stateAfterClear={noneOption}
          />
        </div>
      </div>

      {/* Group 3 - extended, only visible when feature flag is ON */}
      {allow_extended_groups && visibleGroups >= 3 && (
        <div className="row">
          <Stack direction="row" alignItems="center" sx={{ width: "100%", gap: "8px" }}>
            <div className="input-wrap" style={{ flex: 1 }}>
              <AutocompleteMultiSelect
                defaultValue={thirdGroupByOption ?? noneOption}
                label="Group 3"
                options={effectiveThirdGroupByOptions}
                onChange={(value) => setFieldValue("thirdGroupBy", value)}
                stateAfterClear={noneOption}
              />
            </div>
            <IconButton onClick={() => handleRemoveGroup(3)} sx={{ bgcolor: "#f5f5f5", borderRadius: "50%" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </div>
      )}

      {/* Group 4 - extended, only visible when added */}
      {allow_extended_groups && visibleGroups >= 4 && (
        <div className="row">
          <Stack direction="row" alignItems="center" sx={{ width: "100%", gap: "8px" }}>
            <div className="input-wrap" style={{ flex: 1 }}>
              <AutocompleteMultiSelect
                defaultValue={fourthGroupByOption ?? noneOption}
                label="Group 4"
                options={effectiveFourthGroupByOptions}
                onChange={(value) => setFieldValue("fourthGroupBy", value)}
                stateAfterClear={noneOption}
              />
            </div>
            <IconButton onClick={() => handleRemoveGroup(4)} sx={{ bgcolor: "#f5f5f5", borderRadius: "50%" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </div>
      )}

      {/* Group 5 - extended, only visible when originalCurrency is ON and added */}
      {allow_extended_groups && originalCurrency && visibleGroups >= 5 && (
        <div className="row">
          <Stack direction="row" alignItems="center" sx={{ width: "100%", gap: "8px" }}>
            <div className="input-wrap" style={{ flex: 1 }}>
              <AutocompleteMultiSelect
                defaultValue={fifthGroupByOption ?? noneOption}
                label="Group 5"
                options={effectiveFifthGroupByOptions}
                onChange={(value) => setFieldValue("fifthGroupBy", value)}
                stateAfterClear={noneOption}
              />
            </div>
            <IconButton onClick={() => handleRemoveGroup(5)} sx={{ bgcolor: "#f5f5f5", borderRadius: "50%" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </div>
      )}

      {/* Add group parameter button - only when feature flag is ON */}
      {allow_extended_groups && (
        <MaterialTooltip
          isShow={visibleGroups >= maxGroups}
          title={`Maximum of ${originalCurrency ? 5 : 4} groups allowed`}
          placement="top"
        >
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleAddGroup}
            disabled={visibleGroups >= maxGroups}
            sx={{
              textTransform: "none",
              color: "#0a3142",
              bgcolor: "#f5f6f7",
              maxWidth: "150px",
              fontSize: "12px",
              whiteSpace: "nowrap"
            }}
          >
            Add group parameter
          </Button>
        </MaterialTooltip>
      )}

      {/* Last Group (rowBy) - always visible, separate from groupBy */}
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={rowByOption}
            label="Last Group"
            options={rowByOptions}
            onChange={(value) => setFieldValue("rowBy", value)}
          />
        </div>
      </div>

      {groupByHasMeasures && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMeasures
              label="Measures"
              queries={values}
              setQueryValue={setFieldValue}
              showGroups={["measures", "calculatedFields"]}
            />
          </div>
        </div>
      )}
    </div>
  );
};
